import * as THREE from 'three';
import { MASK } from '../world/Physics.js';
import { damageAtRange, PENETRATION_COST, PENETRATION_DAMAGE } from './weapons.data.js';
import { MEANS } from '../game/MatchAuthority.js';

/**
 * Bullet resolution.
 *
 * Hitscan, because at the 5–80 m engagement distances this game actually plays at,
 * travel time is imperceptible and lag-compensating projectiles buys nothing but
 * bugs. The one exception is the marksman rifle, which carries a real (small) time
 * of flight so long shots have a beat before the impact lands — that beat is the
 * whole reason a DMR feels different from a rifle.
 *
 * Two things here matter more than anything else for how the guns feel:
 *
 *  - **First-shot accuracy.** ADS + stationary produces a cone of exactly zero. Not
 *    "very small" — zero. Any spread at all on the first ADS shot and players
 *    correctly conclude the game is lying to them. "ADS" here means the sight has
 *    finished travelling, not that the button is held — see `coneFor`.
 *
 *  - **Spread sampling.** Angles are drawn from a uniform *disc* (r = R·√u), not from
 *    two independent gaussians and not from r = R·u. Sampling radius linearly
 *    clusters shots in the centre and makes the cone feel much tighter than its
 *    stated size, which is a common and confusing bug.
 */

export const TUNING = {
  maxRange: 260,
  maxPenetrationSteps: 4,
  maxWallThickness: 0.62,
  // A bullet exits a surface this far past the far face before re-casting, so it
  // cannot immediately re-hit the collider it just left.
  exitEpsilon: 0.004,
  minPenetrationDamage: 0.12,
  maxTravelTime: 0.22,
};

const _up = new THREE.Vector3(0, 1, 0);
const _alt = new THREE.Vector3(1, 0, 0);

export class Ballistics {
  constructor(ctx, owner = null) {
    this.ctx = ctx;
    this.physics = ctx.physics;
    this.events = ctx.events;
    /**
     * Who these rounds belong to. Left null for the local player's own instance,
     * where `ctx.player` is the answer and is not built yet at construction time.
     * A second player's rounds get their own resolver with their own owner — which
     * is the only thing standing between this and being usable for a remote shot.
     */
    this.owner = owner;
    this._pending = [];
    this._tan = new THREE.Vector3();
    this._bit = new THREE.Vector3();
    this._dir = new THREE.Vector3();
    this._pos = new THREE.Vector3();
    this._back = new THREE.Vector3();
    this._probe = new THREE.Vector3();
    this._stat = { shots: 0, hits: 0, penetrations: 0 };

    ctx.engine.registerUpdate((dt) => this._flush(dt), 'sim');
  }

  /* ------------------------------ spread model ------------------------------ */

  /**
   * Cone half-angle for the next shot, in radians.
   *
   * Accuracy is keyed off `st.adsT` — how far the sight has actually travelled —
   * and never off the aim button. Those are not the same thing: pressing RMB flips
   * the boolean in one frame while the rifle takes 235 ms to come up, and keying off
   * the boolean hands out perfect accuracy for 14 frames while the gun is visibly
   * still at the hip. It also makes `ads.in` a purely cosmetic stat, when it should
   * be one of the sharpest differences between two weapons.
   *
   * Both endpoints stay exact: at `adsT === 1` with no movement the cone is zero to
   * the bit, and bloom — a hip-fire mechanic — is faded out over the same transition
   * so aiming in cannot leave a residue on the first aimed shot.
   *
   * @param weapon weapon definition
   * @param st     { ads, adsT, sprinting, crouching, grounded, speed }
   * @param bloom  accumulated per-shot bloom in radians
   */
  static coneFor(weapon, st, bloom = 0) {
    const s = weapon.spread;
    // Fall back to the boolean only for callers that have no transition to offer.
    const raw = st.adsT ?? (st.ads ? 1 : 0);
    const t = THREE.MathUtils.clamp(raw, 0, 1);
    // Smoothstep, so the endpoints are flat: the last few frames of the animation
    // are where the cone actually closes, which is what the player sees.
    const k = t * t * (3 - 2 * t);

    const moveT = THREE.MathUtils.clamp(st.speed / 4.2, 0, 1);

    let hip;
    if (st.sprinting) {
      hip = s.hipSprint;
    } else {
      hip = THREE.MathUtils.lerp(s.hip, s.hipMoving, moveT);
      if (st.crouching) hip *= s.crouchScale;
    }

    let aimed = THREE.MathUtils.lerp(s.ads, s.adsMoving, moveT);
    if (st.crouching) aimed *= s.crouchScale;

    let cone = THREE.MathUtils.lerp(hip, aimed, k);
    if (!st.grounded) cone = Math.max(cone, s.hip) * s.airScale;
    return cone + bloom * (1 - k);
  }

  /* --------------------------------- firing --------------------------------- */

  /**
   * Resolve one trigger pull.
   *
   * @param {object} o
   *   weapon, origin (Vector3), dir (Vector3, normalised), cone (radians),
   *   shooter (actor to ignore), rng (optional () => [0,1)), damageScale
   * @returns {Array} per-pellet results, each { end, hit, actor, dist }
   */
  fire(o) {
    const weapon = o.weapon;
    const rng = o.rng ?? this.ctx.rng;
    const pellets = weapon.pellets ?? 1;
    const results = [];

    /**
     * A shell's own pattern, kept separate from the aim cone.
     *
     * Without this a shotgun has to buy its scatter out of `spread.ads`, which
     * breaks the one rule the whole accuracy model rests on: fully-shouldered and
     * stationary is a cone of exactly zero. The two are not the same quantity.
     * The aim cone is the shooter's uncertainty about where the barrel points and
     * must go to zero when they have done everything right; pellet spread is the
     * shell throwing nine projectiles through one choke and never goes away, not
     * when aimed, not when crouched, not when prone.
     *
     * Added in quadrature because the two are independent: a hip-fired shotgun's
     * pattern is the aim error and the choke compounding, not either one twice.
     *
     * A fitted choke may close the pattern as the stock comes into the shoulder —
     * `pelletSpreadAdsScale`, 1 by default and so a no-op for every weapon that has
     * no choke on it. Note what this does *not* do: the aim cone is untouched, and at
     * `adsT === 1` with no movement it is still exactly zero. A choke narrows the
     * shell's own pattern; it cannot make a shotgun's aim more certain than any other
     * weapon's, because nothing can.
     */
    let ps = weapon.pelletSpread ?? 0;
    if (ps > 0 && weapon.pelletSpreadAdsScale != null && weapon.pelletSpreadAdsScale !== 1) {
      const t = THREE.MathUtils.clamp(o.adsT ?? (o.ads ? 1 : 0), 0, 1);
      ps *= THREE.MathUtils.lerp(1, weapon.pelletSpreadAdsScale, t * t * (3 - 2 * t));
    }
    const cone = ps > 0 ? Math.sqrt(o.cone * o.cone + ps * ps) : o.cone;

    for (let i = 0; i < pellets; i++) {
      // Pellet 0 of a single-projectile weapon flies down the exact aim axis when
      // the cone is zero; the disc sample degenerates to the centre anyway, but we
      // skip the maths so floating point can never introduce a wobble.
      this._dir.copy(o.dir);
      if (cone > 1e-6) this._sampleCone(this._dir, cone, rng);

      if (weapon.projectile) {
        // Time of flight: probe once for the range, then resolve the real trace
        // after the bullet would have arrived. One extra raycast on a 340 RPM
        // semi-auto is free, and the delay is what gives a long shot its beat.
        const probeDir = this._dir.clone();
        const probeOrigin = o.origin.clone();
        const probe = this.physics.raycast(probeOrigin, probeDir, TUNING.maxRange, MASK.ALL, o.shooter);
        const dist = probe.hit ? probe.dist : 60;
        const shooter = o.shooter;
        const scale = o.damageScale ?? 1;
        this.defer(dist / (weapon.muzzleVelocity || 800), () =>
          this._trace(weapon, probeOrigin, probeDir, shooter, scale)
        );
        results.push({
          end: probe.hit ? probe.point.clone() : probeOrigin.clone().addScaledVector(probeDir, 90),
          hit: probe.hit,
          actor: probe.actor,
          dist,
          material: probe.materialId,
          delayed: true,
        });
        continue;
      }

      results.push(this._trace(weapon, o.origin, this._dir, o.shooter, o.damageScale ?? 1));
    }

    this._stat.shots += pellets;
    return results;
  }

  /** Perturb `dir` (in place) inside a cone of half-angle `cone`, uniform over the disc. */
  _sampleCone(dir, cone, rng) {
    // Tangent basis. Guard against dir being parallel to world up.
    const ref = Math.abs(dir.y) > 0.94 ? _alt : _up;
    this._tan.crossVectors(dir, ref).normalize();
    this._bit.crossVectors(dir, this._tan).normalize();

    const r = Math.tan(cone) * Math.sqrt(rng());
    const a = rng() * Math.PI * 2;
    dir.addScaledVector(this._tan, Math.cos(a) * r).addScaledVector(this._bit, Math.sin(a) * r).normalize();
    return dir;
  }

  /* ------------------------------- single trace ----------------------------- */

  _trace(weapon, origin, dir, shooter, damageScale) {
    const phys = this.physics;
    const pos = this._pos.copy(origin);
    const d = dir.clone(); // kept: results are consumed asynchronously by FX
    let travelled = 0;
    let power = weapon.penetration ?? 0;
    let dmgMul = damageScale;
    let steps = 0;

    const out = { end: null, hit: false, actor: null, dist: 0, material: null };

    while (steps++ < TUNING.maxPenetrationSteps) {
      const remaining = TUNING.maxRange - travelled;
      if (remaining <= 0) break;

      const r = phys.raycast(pos, d, remaining, MASK.ALL, shooter);
      if (!r.hit) {
        out.end = pos.clone().addScaledVector(d, Math.min(remaining, 90));
        break;
      }

      travelled += r.dist;
      out.end = r.point.clone();
      out.hit = true;
      out.dist = travelled;
      out.material = r.materialId;

      const base = damageAtRange(weapon, travelled) * dmgMul;

      if (r.actor) {
        out.actor = r.actor;
        this._resolveActor(weapon, r, d, base, travelled);
        // Bullets stop in bodies unless the round is genuinely powerful.
        const cost = PENETRATION_COST.flesh * 0.35;
        if (power <= cost) break;
        power -= cost;
        dmgMul *= PENETRATION_DAMAGE.flesh;
        pos.copy(r.point).addScaledVector(d, 0.42);
        continue;
      }

      this._emitSurface(r, d, weapon, base);

      /* ----------------------------- penetration ---------------------------- */
      const mat = r.materialId ?? 'concrete';
      const costPerM = PENETRATION_COST[mat] ?? 999;
      if (power <= 0) break;

      const exit = this._findExit(r.point, d, shooter);
      if (!exit) break; // thicker than we can measure -> treat as solid
      const cost = exit.thickness * costPerM;
      if (cost > power) break;

      power -= cost;
      // Damage retained scales with how much of the bullet's budget survived.
      const retain = THREE.MathUtils.lerp(
        PENETRATION_DAMAGE[mat] ?? 0.4,
        1,
        1 - THREE.MathUtils.clamp(cost / Math.max(power + cost, 1e-4), 0, 1)
      );
      dmgMul *= Math.max(TUNING.minPenetrationDamage, retain);
      this._stat.penetrations++;

      // Exit spall on the far side reads as the bullet coming through.
      this.events.emit('hit:surface', {
        point: exit.point.clone(),
        normal: exit.normal.clone(),
        material: mat,
        entity: null,
        penetrationExit: true,
        weapon: weapon.id,
        damage: 0,
      });

      pos.copy(exit.point).addScaledVector(d, TUNING.exitEpsilon);
      travelled += exit.thickness + TUNING.exitEpsilon;
    }

    if (!out.end) out.end = pos.clone().addScaledVector(d, 90);
    if (out.hit) this._stat.hits++;
    return out;
  }

  _resolveActor(weapon, r, dir, baseDamage, dist) {
    const hb = r.hitbox;
    const headshot = hb?.tag === 'head';
    const scale = hb?.damageScale ?? 1;
    /**
     * Optional per-weapon hitbox multiplier, applied on top of the actor's own.
     *
     * Exists for one weapon and one reason. A bolt gun's promise is "one shot, one
     * kill on the upper torso", which needs a flat damage figure above the enemy's
     * whole health pool — and the chest, stomach, arm and leg boxes all sit at or
     * below 1.0 on the actor side, so that same figure would one-shot a shin. This
     * lets the weapon say where on a body its promise applies. Weapons without the
     * table are completely unaffected.
     */
    const wScale = weapon.hitboxScale?.[hb?.tag] ?? 1;
    const damage = baseDamage * wScale;
    const point = r.point.clone();
    const normal = r.normal.clone();
    const shotDir = dir.clone();

    /**
     * `damage` here is the pre-hitbox value. The match applies the actor's own
     * hitbox scale, which is what lets an armoured enemy override it per-limb — and
     * is now also the only place that multiplication happens, rather than once here
     * for the event and once inside the actor for the health.
     */
    const res = this.ctx.match?.requestDamage({
      target: r.actor,
      source: this.owner ?? this.ctx.player,
      amount: damage,
      hitbox: hb,
      point,
      normal,
      dir: shotDir,
      means: MEANS.BULLET,
      weapon: weapon.id,
      dist,
    });
    if (res?.blocked) return;

    this.events.emit('hit:actor', {
      actor: r.actor,
      point,
      normal,
      damage: res?.amount ?? damage * scale,
      headshot,
      dir: shotDir,
      hitbox: hb,
      weapon: weapon.id,
      source: this.owner ?? this.ctx.player,
    });
  }

  _emitSurface(r, dir, weapon, damage) {
    this.events.emit('hit:surface', {
      point: r.point.clone(),
      normal: r.normal.clone(),
      material: r.materialId ?? 'concrete',
      entity: r.entity ?? null,
      dir: dir.clone(),
      weapon: weapon.id,
      damage,
    });
  }

  /**
   * Find where a bullet leaves the surface it just entered.
   *
   * Cast backwards from a point beyond the far face: the first thing the reversed
   * ray meets is the exit plane. If the reversed ray hits immediately (t≈0) the
   * start point was still inside the collider, which means the wall is thicker than
   * `maxWallThickness` — correctly reported as unpenetrable.
   */
  _findExit(entry, dir, shooter) {
    const T = TUNING.maxWallThickness;
    this._probe.copy(entry).addScaledVector(dir, T);
    this._back.copy(dir).negate();
    const r = this.physics.raycast(this._probe, this._back, T, MASK.WORLD | MASK.PROP, shooter);
    if (!r.hit) return null;
    const thickness = T - r.dist;
    if (thickness <= 1e-4 || thickness >= T - 1e-3) return null;
    return { point: r.point, normal: r.normal, thickness };
  }

  /* --------------------------- deferred (projectile) ------------------------- */

  /** Queue a resolved shot so its impact lands after a short time of flight. */
  defer(delay, fn) {
    this._pending.push({ t: Math.min(delay, TUNING.maxTravelTime), fn });
  }

  _flush(dt) {
    if (!this._pending.length) return;
    for (let i = this._pending.length - 1; i >= 0; i--) {
      const p = this._pending[i];
      p.t -= dt;
      if (p.t <= 0) {
        this._pending.splice(i, 1);
        try {
          p.fn();
        } catch (err) {
          console.error('[ballistics] deferred shot threw', err);
        }
      }
    }
  }

  get stats() {
    return this._stat;
  }
}
